import { useCallback, useEffect, useMemo, useState } from "react"
import api from "../utils/api"
import Icon from "./Icon"
import AsyncActionModal from "./AsyncActionModal"
import { formatMoney } from "../utils/format"
import { getApiErrorMessage } from "../utils/errorMessage"
import {
  buildRequestFingerprint,
  clearStableRequestKey,
  getStableRequestKey,
} from "../utils/requestKey"

// Send money from the UT wallet to one billing company before it bills you.
//
// Self-contained so it can sit inside Global Wallet Settings and inside the UT
// Bills screen without either host repeating the money rules. The company list
// opens on companies that already bill this person; typing searches every
// registered company. A company that bills more than one thing (water, trash)
// can be narrowed to a single service, so the credit is only spent on that.
const SEARCH_DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 2
const REQUEST_STORAGE_KEY = "utbills:prepay"
const ALL_SERVICES = "all"

function PrepayForm({ onDone, onCancel, submitLabel = "Confirm prepayment" }) {
  const [walletBalance, setWalletBalance] = useState(null)
  const [amount, setAmount] = useState("")
  const [query, setQuery] = useState("")
  const [issuers, setIssuers] = useState([])
  const [issuersLoading, setIssuersLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [accountId, setAccountId] = useState(ALL_SERVICES)
  const [status, setStatus] = useState("")
  const [result, setResult] = useState(null)
  const [error, setError] = useState("")
  const [listError, setListError] = useState("")

  const loadWallet = useCallback(async () => {
    try {
      const response = await api.get("/utilities/prepayments")
      setWalletBalance(Number(response?.data?.walletBalance || 0))
    } catch (err) {
      setWalletBalance(0)
    }
  }, [])

  useEffect(() => { loadWallet() }, [loadWallet])

  const loadIssuers = useCallback(async (search) => {
    setIssuersLoading(true)
    try {
      const response = await api.get(
        `/utilities/prepayments/issuers${search ? `?q=${encodeURIComponent(search)}` : ""}`
      )
      setIssuers(Array.isArray(response?.data?.issuers) ? response.data.issuers : [])
      setListError("")
    } catch (err) {
      setListError(getApiErrorMessage(err, "Failed to load billing companies."))
    } finally {
      setIssuersLoading(false)
    }
  }, [])

  useEffect(() => {
    const trimmed = query.trim()
    // A one-letter query would search the whole company table on every
    // keystroke, so short input keeps showing the known companies.
    const search = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ""
    const timer = setTimeout(() => loadIssuers(search), search ? SEARCH_DEBOUNCE_MS : 0)
    return () => clearTimeout(timer)
  }, [query, loadIssuers])

  // What the chosen company bills this person for. One service means there is
  // nothing to choose, so the picker stays hidden.
  useEffect(() => {
    if (!selected?._id) {
      setAccounts([])
      setAccountId(ALL_SERVICES)
      return
    }
    let active = true
    setAccountsLoading(true)
    setAccountId(ALL_SERVICES)
    api
      .get(`/utilities/prepayments/issuers/${encodeURIComponent(selected._id)}/accounts`)
      .then((response) => {
        if (!active) return
        setAccounts(Array.isArray(response?.data?.accounts) ? response.data.accounts : [])
      })
      .catch(() => { if (active) setAccounts([]) })
      .finally(() => { if (active) setAccountsLoading(false) })
    return () => { active = false }
  }, [selected])

  const numericAmount = Number(amount)
  const amountIsValid = Number.isFinite(numericAmount) && numericAmount > 0
  const balance = Number(walletBalance) || 0
  const notEnoughFunds = amountIsValid && walletBalance !== null && numericAmount > balance
  const busy = status === "loading"
  const canSubmit = amountIsValid && !notEnoughFunds && Boolean(selected) && !busy

  const knownIssuers = useMemo(() => issuers.filter((issuer) => issuer.known), [issuers])
  const otherIssuers = useMemo(() => issuers.filter((issuer) => !issuer.known), [issuers])
  const chosenAccount = accounts.find((account) => String(account._id) === String(accountId)) || null
  const targetLabel = chosenAccount ? `${selected?.name} · ${chosenAccount.label}` : selected?.name

  const submit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return
    // One key per (company, service, amount). A double tap or a retry after a
    // dropped reply replays the same prepayment instead of sending the money
    // twice, while changing any field starts a fresh request.
    const requestKey = getStableRequestKey({
      storageKey: REQUEST_STORAGE_KEY,
      fingerprint: buildRequestFingerprint({
        providerId: selected._id,
        accountId: chosenAccount?._id || null,
        amount: numericAmount,
      }),
      prefix: "ut-prepay",
    })
    setStatus("loading")
    setError("")
    try {
      const response = await api.post("/utilities/prepayments", {
        providerId: selected._id,
        accountId: chosenAccount?._id || null,
        amount: numericAmount,
        idempotencyKey: requestKey,
      })
      clearStableRequestKey({ storageKey: REQUEST_STORAGE_KEY, requestKey })
      const data = response?.data || {}
      setResult({
        target: targetLabel,
        amount: numericAmount,
        appliedToOpenBills: Number(data.appliedToOpenBills || 0),
        waiting: Math.max(0, numericAmount - Number(data.appliedToOpenBills || 0)),
        walletBalance: Number(data.walletBalance || 0),
      })
      setStatus("success")
      setAmount("")
      setWalletBalance(Number(data.walletBalance || 0))
      if (typeof onDone === "function") onDone(data)
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to prepay."))
      setStatus("error")
    }
  }

  const closeResult = () => {
    setStatus("")
    setError("")
    setResult(null)
    loadWallet()
  }

  return (
    <>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">UT wallet</p>
          <p className="text-base font-black tabular-nums text-slate-800">
            {walletBalance === null ? "…" : formatMoney(balance)}
          </p>
        </div>

        <div>
          <label htmlFor="prepay-amount" className="block text-sm font-bold text-slate-700">
            Amount to prepay
          </label>
          <input
            id="prepay-amount"
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0.00"
            className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base font-semibold focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            required
          />
          {notEnoughFunds && (
            <p className="mt-1.5 text-xs font-semibold text-red-600">
              Your UT wallet only has {formatMoney(balance)}.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="prepay-search" className="block text-sm font-bold text-slate-700">
            Who do you want to prepay?
          </label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2">
            <Icon name="search" size="0.8rem" className="shrink-0 text-slate-400" />
            <input
              id="prepay-search"
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search all companies…"
              className="min-w-0 flex-1 bg-transparent text-sm focus:outline-none"
            />
          </div>

          <div className="mt-2 overflow-hidden rounded-xl border border-slate-200">
            {issuersLoading ? (
              <p className="px-3 py-4 text-center text-xs text-slate-400">Loading…</p>
            ) : listError ? (
              <p className="px-3 py-4 text-center text-xs text-red-600">{listError}</p>
            ) : issuers.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-slate-400">
                {query.trim() ? "No company matches that search." : "No billing companies found yet."}
              </p>
            ) : (
              <div className="max-h-48 overflow-y-auto">
                <IssuerGroup
                  label="Companies that bill you"
                  issuers={knownIssuers}
                  selected={selected}
                  onSelect={setSelected}
                />
                <IssuerGroup
                  label="Other registered companies"
                  issuers={otherIssuers}
                  selected={selected}
                  onSelect={setSelected}
                />
              </div>
            )}
          </div>
        </div>

        {selected && (accountsLoading || accounts.length > 1) && (
          <div>
            <label htmlFor="prepay-account" className="block text-sm font-bold text-slate-700">
              What is this for?
            </label>
            <select
              id="prepay-account"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              disabled={accountsLoading}
              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            >
              <option value={ALL_SERVICES}>Any bill from this company</option>
              {accounts.map((account) => (
                <option key={account._id} value={account._id}>{account.label}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-500">
              Pick one service and the money can only pay that service&apos;s bills.
            </p>
          </div>
        )}

        {selected && amountIsValid && !notEnoughFunds && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3">
            <p className="text-sm font-semibold text-emerald-900">
              {formatMoney(numericAmount)} will leave your UT wallet and go to {targetLabel} as prepaid credit.
            </p>
            <p className="mt-1 text-xs text-emerald-800">
              If you already owe them, it clears those bills first. Your other bills keep being paid
              the same way as now.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? "Sending…" : submitLabel}
          </button>
        </div>
      </form>

      <AsyncActionModal
        open={Boolean(status)}
        status={status || "loading"}
        title={
          status === "loading" ? "Sending…" : status === "error" ? "Prepayment Failed" : "Prepayment Sent"
        }
        statusLabel={
          status === "loading" ? "Sending…" : status === "error" ? "Prepayment Failed" : "Prepayment Sent"
        }
        message={
          status === "loading"
            ? "Please wait while we move your money."
            : status === "error"
              ? error
              : `${formatMoney(result?.amount || 0)} is now held by ${result?.target} for your bills.`
        }
        details={
          status === "success" && result
            ? [
                { label: "Amount", value: formatMoney(result.amount) },
                { label: "Sent to", value: result.target },
                ...(result.appliedToOpenBills > 0
                  ? [{ label: "Cleared bills already sent", value: formatMoney(result.appliedToOpenBills) }]
                  : []),
                { label: "Waiting for future bills", value: formatMoney(result.waiting) },
                { label: "UT wallet now", value: formatMoney(result.walletBalance) },
              ]
            : []
        }
        onClose={closeResult}
        primaryLabel={status === "loading" ? undefined : "Done"}
        onPrimary={closeResult}
      />
    </>
  )
}

function IssuerGroup({ label, issuers, selected, onSelect }) {
  if (!issuers.length) return null
  return (
    <>
      <p className="bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>
      {issuers.map((issuer) => {
        const isSelected = String(selected?._id || "") === String(issuer._id)
        return (
          <button
            key={issuer._id}
            type="button"
            onClick={() => onSelect(issuer)}
            aria-pressed={isSelected}
            className={`flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-left transition-colors ${
              isSelected ? "bg-emerald-50" : "hover:bg-slate-50"
            }`}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-800">{issuer.name}</p>
              {issuer.location && (
                <p className="truncate text-[11px] text-slate-400">{issuer.location}</p>
              )}
            </div>
            {isSelected && <Icon name="check-circle-fill" size="0.9rem" className="shrink-0 text-emerald-600" />}
          </button>
        )
      })}
    </>
  )
}

export default PrepayForm
